import React, { useRef, useEffect, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import RegionsPlugin from "wavesurfer.js/dist/plugin/wavesurfer.regions.min.js";
import { fetchFile } from "@ffmpeg/ffmpeg";
import { attachWaveformPanScroll } from "../utils/waveformPanScroll";

const SNAP_THRESHOLD = 0.05;

const PlayIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 5V19L19 12L8 5Z" />
  </svg>
);

const PauseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 19H10V5H6V19ZM14 5V19H18V5H14Z" />
  </svg>
);

const WaveformTrimmer = ({ file, fileSettings, onUpdateFileSettings, disabled, ffmpeg }) => {
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const panScrollCleanupRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [hasWaveformError, setHasWaveformError] = useState(false);
  const [isWaveformRendering, setIsWaveformRendering] = useState(false);
  
  const [playbackRate, setPlaybackRate] = useState(fileSettings.playbackRate || 1);
  const [currentVolume, setCurrentVolume] = useState(fileSettings.volume);

  const calculateMinPxPerSec = (audioDuration) => {
    if (!waveformRef.current) return 10;
    const containerWidth = waveformRef.current.offsetWidth;
    if (containerWidth === 0) return 10;
    const minPxPerSec = audioDuration > 0 ? containerWidth / audioDuration : 10;
    return Math.max(10, minPxPerSec);
  };
  
  const mapSliderValueToPlaybackRate = (sliderValue) => {
    const rate = 1.0 + (sliderValue / 100) * 0.5;
    return rate;
  };
  
  const mapPlaybackRateToSliderValue = (rate) => {
    const sliderValue = (rate - 1.0) / 0.5 * 100;
    return sliderValue;
  };

  useEffect(() => {
    if (!file) return;

    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
    }
    
    setHasWaveformError(false);
    setIsLoading(true);
    setLoadProgress(0);
    setIsWaveformRendering(false);

    if (!waveformRef.current) return;
    
    let wavesurfer;

    const loadWaveform = (audioFile) => {
      return new Promise((resolve, reject) => {
        wavesurfer = WaveSurfer.create({
          container: waveformRef.current,
          waveColor: "#a3b0ff",
          progressColor: "#5b6ad1",
          cursorColor: "#2c3a8e",
          barWidth: 3,
          height: 100,
          responsive: true,
          autoCenter: false,
          hideScrollbar: false,
          normalize: true,
          plugins: [
            RegionsPlugin.create({
              regionsMinLength: 0.1,
              dragSelection: false,
              color: "rgba(100, 120, 255, 0.2)",
            }),
          ],
        });

        wavesurferRef.current = wavesurfer;
        
        let fileUrl;
        if (audioFile instanceof Blob || audioFile instanceof File) {
          fileUrl = URL.createObjectURL(audioFile);
        } else {
          console.error("Invalid audio file type for WaveSurfer");
          reject(new Error("Invalid audio file type"));
          return;
        }

        wavesurfer.load(fileUrl);
        
        wavesurfer.on("loading", (percent) => {
          setLoadProgress(percent);
          if (percent === 100) {
            setIsLoading(false);
            setIsWaveformRendering(true);
          }
        });

        wavesurfer.on('error', (err) => {
          console.error("WaveSurfer error:", err);
          setHasWaveformError(true);
          setIsLoading(false);
          setIsWaveformRendering(false);
          reject(err);
        });
        
        wavesurfer.on("ready", () => {
          const audioDuration = wavesurfer.getDuration();
          setDuration(audioDuration);
          setIsWaveformRendering(false);
          wavesurfer.setVolume(currentVolume);

          setTimeout(() => {
            if (wavesurferRef.current && waveformRef.current) {
              const minPxPerSec = calculateMinPxPerSec(audioDuration);
              wavesurferRef.current.zoom(minPxPerSec);
            }
          }, 50);

          let initialTrimEnd = fileSettings.trimEnd;
          if (initialTrimEnd === null || initialTrimEnd === 0) {
            initialTrimEnd = audioDuration;
            onUpdateFileSettings({ trimEnd: initialTrimEnd });
          }

          const region = wavesurfer.regions.add({
            start: fileSettings.trimStart || 0,
            end: initialTrimEnd,
            color: "rgba(100, 120, 255, 0.2)",
            resize: true,
            drag: true,
          });

          region.on("update", () => {
            const newStart = Math.max(0, region.start);
            const newEnd = Math.min(audioDuration, region.end);
            if (region.start !== newStart || region.end !== newEnd) {
              region.update({ start: newStart, end: newEnd });
            }
          });

          region.on("update-end", () => {
            const newTrimStart = Math.max(0, region.start);
            const newTrimEnd = Math.min(audioDuration, region.end);
            onUpdateFileSettings({ trimStart: newTrimStart, trimEnd: newTrimEnd });
          });

          panScrollCleanupRef.current?.();
          panScrollCleanupRef.current = attachWaveformPanScroll(wavesurfer, {
            container: waveformRef.current,
            getRegion: () => Object.values(wavesurfer.regions.list)[0],
          });
          
          resolve(wavesurfer);
        });

        wavesurfer.on("play", () => setIsPlaying(true));
        wavesurfer.on("pause", () => setIsPlaying(false));
        wavesurfer.on("finish", () => setIsPlaying(false));
        wavesurfer.on("audioprocess", (time) => {
          setCurrentTime(time);
        });
      });
    };

    const handleFileLoading = async () => {
      const isAiff = file.name.toLowerCase().endsWith(".aiff") || file.name.toLowerCase().endsWith(".aif");

      let audioSrc = file;

      if (isAiff && ffmpeg) {
        console.log("AIFFファイルを検出しました。波形表示のためにWAVに事前変換します。");
        setIsLoading(true);
        setLoadProgress(0);

        try {
          const inputFileName = `input_${file.name}`;
          const outputFileName = `waveform_temp.wav`;

          await ffmpeg.FS('writeFile', inputFileName, await fetchFile(file));
          await ffmpeg.run('-i', inputFileName, '-c:a', 'pcm_s16le', outputFileName);
          const data = await ffmpeg.FS('readFile', outputFileName);
          
          audioSrc = new Blob([data.buffer], { type: 'audio/wav' });

          await ffmpeg.FS('unlink', inputFileName);
          await ffmpeg.FS('unlink', outputFileName);
        } catch (error) {
          console.error("AIFFの事前変換中にエラーが発生しました:", error);
          setHasWaveformError(true);
          setIsLoading(false);
          return;
        }
      }
      
      try {
        await loadWaveform(audioSrc);
      } catch (error) {
        console.error("波形データの読み込みに失敗しました。", error);
      }
    };
    
    handleFileLoading();

    return () => {
      panScrollCleanupRef.current?.();
      panScrollCleanupRef.current = null;
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
      }
    };
  }, [file, ffmpeg]);

  useEffect(() => {
    setCurrentVolume(fileSettings.volume);
    setPlaybackRate(fileSettings.playbackRate || 1);
  }, [fileSettings.volume, fileSettings.playbackRate]);

  useEffect(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.setVolume(currentVolume);
    }
  }, [currentVolume]);
  
  useEffect(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.setPlaybackRate(playbackRate);
    }
  }, [playbackRate]);
  
  useEffect(() => {
    if (wavesurferRef.current && wavesurferRef.current.regions && Object.values(wavesurferRef.current.regions.list).length > 0) {
      const region = Object.values(wavesurferRef.current.regions.list)[0];
      if (region) {
        region.update({ start: fileSettings.trimStart, end: fileSettings.trimEnd });
      }
    }
  }, [fileSettings.trimStart, fileSettings.trimEnd]);

  const handlePlay = () => {
    if (wavesurferRef.current) {
      wavesurferRef.current.play();
    }
  };

  const handlePause = () => {
    if (wavesurferRef.current) {
      wavesurferRef.current.pause();
    }
  };

  const handleSkip = (seconds) => {
    if (wavesurferRef.current) {
      wavesurferRef.current.skip(seconds);
    }
  };

  const handlePlaybackRateChange = (e) => {
    const sliderValue = parseFloat(e.target.value);
    let newRate = mapSliderValueToPlaybackRate(sliderValue);
    
    if (Math.abs(newRate - 1) < SNAP_THRESHOLD) {
      newRate = 1;
    }
    
    setPlaybackRate(newRate);
    onUpdateFileSettings({ playbackRate: newRate });
  };
  
  const handleZoomIn = () => {
    if (wavesurferRef.current) {
      const currentZoom = wavesurferRef.current.params.minPxPerSec;
      wavesurferRef.current.zoom(currentZoom + 20);
    }
  };

  const handleZoomOut = () => {
    if (wavesurferRef.current) {
      const currentZoom = wavesurferRef.current.params.minPxPerSec;
      wavesurferRef.current.zoom(Math.max(1, currentZoom - 20));
    }
  };
  
  const handleZoomReset = () => {
    if (wavesurferRef.current) {
      const audioDuration = wavesurferRef.current.getDuration();
      const newZoom = calculateMinPxPerSec(audioDuration);
      wavesurferRef.current.zoom(newZoom);
    }
  }

  const handleVolumeChange = (e) => {
    let newVolume = parseFloat(e.target.value);
    if (Math.abs(newVolume - 1) < SNAP_THRESHOLD) {
      newVolume = 1;
    }
    setCurrentVolume(newVolume);
    onUpdateFileSettings({ volume: newVolume });
  };

  const handleTrimStartChange = (e) => {
    const newStart = parseFloat(e.target.value);
    onUpdateFileSettings({ trimStart: newStart, trimEnd: fileSettings.trimEnd });
  };

  const handleTrimEndChange = (e) => {
    const newEnd = parseFloat(e.target.value);
    onUpdateFileSettings({ trimEnd: newEnd, trimStart: fileSettings.trimStart });
  };
  
  const handleSetTrimStart = () => {
    if (wavesurferRef.current) {
      const newTime = wavesurferRef.current.getCurrentTime();
      let newTrimEnd = fileSettings.trimEnd;
      if (newTime >= newTrimEnd) {
        newTrimEnd = newTime + 0.01;
      }
      onUpdateFileSettings({ trimStart: newTime, trimEnd: newTrimEnd });
    }
  };
  
  const handleSetTrimEnd = () => {
    if (wavesurferRef.current) {
      const newTime = wavesurferRef.current.getCurrentTime();
      let newTrimStart = fileSettings.trimStart;
      if (newTime <= newTrimStart) {
        newTrimStart = Math.max(0, newTime - 0.01);
      }
      onUpdateFileSettings({ trimStart: newTrimStart, trimEnd: newTime });
    }
  };

  const loading = isLoading || isWaveformRendering;

  return (
    <div>
      <div className="trim-settings-group">
        <h3>音声トリミング ✂️</h3>
        {hasWaveformError ? (
          <div style={{ textAlign: 'center', padding: '50px 0', color: '#dc3545', border: '2px dashed #dc3545', borderRadius: '5px' }}>
            <p>波形データの生成に失敗しました。ファイルが破損しているか、非対応の形式の可能性があります。</p>
            <p>再生ボタンで音声を確認し、時間指定でトリミングしてください。</p>
          </div>
        ) : isLoading ? (
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <p>音声波形を読み込み中... {loadProgress}%</p>
            <progress value={loadProgress} max="100" style={{ width: '80%' }}></progress>
          </div>
        ) : isWaveformRendering ? (
          <div style={{ textAlign: 'center', padding: '50px 0' }}>
            <div className="loading-notes">
              <span>&#9835;</span>
              <span>&#9836;</span>
              <span>&#9835;</span>
            </div>
            <span>波形を描写中、暫くお待ちください。</span>
          </div>
        ) : null}

        <div
          className="waveform-container"
          ref={waveformRef}
          style={{ width: "100%", display: hasWaveformError || loading ? 'none' : 'block' }}
        ></div>
        
        <div 
          style={{
            display: "flex", 
            justifyContent: "center", 
            alignItems: "center", 
            gap: "20px",
            flexWrap: "wrap",
            marginTop: "20px" 
          }}
        >
          {/* 開始位置グループ */}
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <button 
                onClick={handleSetTrimStart} 
                disabled={disabled || loading} 
                className="control-button"
                style={{ width: '40px', minWidth: '40px', padding: '0' }}
                title="現在の再生位置を開始点に設定"
                aria-label="現在の再生位置を開始点に設定"
            >
                <span style={{fontSize: '1.5em', color: '#ff6b6b', fontWeight: 'bold'}}>{'{'}</span>
            </button>
            <label htmlFor="cac-trim-start">開始:</label>
            <input
              id="cac-trim-start"
              name="trim-start"
              type="number"
              step="0.01"
              value={fileSettings.trimStart !== null ? fileSettings.trimStart.toFixed(2) : ''}
              onChange={handleTrimStartChange}
              style={{ width: "70px", padding: "8px" }}
              disabled={disabled || loading}
            />
          </div>

          {/* 終了位置グループ */}
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <label htmlFor="cac-trim-end">終了:</label>
            <input
              id="cac-trim-end"
              name="trim-end"
              type="number"
              step="0.01"
              value={fileSettings.trimEnd !== null ? fileSettings.trimEnd.toFixed(2) : ''}
              onChange={handleTrimEndChange}
              style={{ width: "70px", padding: "8px" }}
              disabled={disabled || loading}
            />
            <button 
                onClick={handleSetTrimEnd} 
                disabled={disabled || loading} 
                className="control-button"
                style={{ width: '40px', minWidth: '40px', padding: '0' }}
                title="現在の再生位置を終了点に設定"
                aria-label="現在の再生位置を終了点に設定"
            >
                <span style={{fontSize: '1.5em', color: '#4d94ff', fontWeight: 'bold'}}>{'}'}</span>
            </button>
          </div>
        </div>

      </div>

      <div className="trim-settings-group">
        <h3>再生コントロール 🎵</h3>

        <div style={{ textAlign: 'center', margin: '10px 0 15px 0' }}>
            <span style={{ whiteSpace: 'nowrap', fontSize: '0.9em' }}>
                再生位置: {currentTime.toFixed(2)}s / 長さ: {duration.toFixed(2)}s
            </span>
        </div>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '15px'
        }}>

          {/* 再生ボタン群 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button onClick={() => handleSkip(-30)} disabled={disabled || loading} className="control-button">-30s</button>
            <button onClick={() => handleSkip(-5)} disabled={disabled || loading} className="control-button">-5s</button>
            {isPlaying ? (
              <button onClick={handlePause} disabled={disabled || loading} className="control-button" aria-label="一時停止"><PauseIcon /></button>
            ) : (
              <button onClick={handlePlay} disabled={disabled || loading} className="control-button" aria-label="再生"><PlayIcon /></button>
            )}
            <button onClick={() => handleSkip(5)} disabled={disabled || loading} className="control-button">+5s</button>
            <button onClick={() => handleSkip(30)} disabled={disabled || loading} className="control-button">+30s</button>
          </div>

          {/* 音量・倍速スライダー群 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <div className="volume-control">
              <label htmlFor="cac-volume">音量:</label>
              <div className="slider-container">
                <input
                  id="cac-volume"
                  name="volume"
                  type="range"
                  min="0"
                  max="2"
                  step="0.01"
                  value={currentVolume}
                  onChange={handleVolumeChange}
                  disabled={disabled || loading}
                />
              </div>
              <span>{(currentVolume * 100).toFixed(0)}%</span>
            </div>
            <div className="volume-control">
              <label htmlFor="cac-playback-rate">倍速:</label>
              <div className="slider-container">
                <input
                  id="cac-playback-rate"
                  name="playback-rate"
                  type="range"
                  min="-100"
                  max="100"
                  step="1"
                  value={mapPlaybackRateToSliderValue(playbackRate)}
                  onChange={handlePlaybackRateChange}
                  disabled={disabled || loading}
                />
              </div>
              <span>{playbackRate.toFixed(1)}x</span>
            </div>
          </div>
          
          {/* ズームボタン群 */}
          {!loading && !hasWaveformError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button onClick={handleZoomIn} disabled={disabled} className="control-button">拡大 (+)</button>
              <button onClick={handleZoomOut} disabled={disabled} className="control-button">縮小 (-)</button>
              <button onClick={handleZoomReset} disabled={disabled} className="control-button">幅に合わせる</button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default WaveformTrimmer;